/* Call Scripts: what she says on a call, word for word (Grow).
 *
 * Built 2026-09-16 from docs/call-scripts-spec.md, read through HER ANSWERS at
 * the top of that file — they override its body:
 *   a. Core scripts (stored id "onboarding") serve CORE offers (Trust
 *      Building → Trust Building, Continuation → Continuation).
 *   b. Done is her mark on the SCRIPT; a section is written or not.
 *   c–e. The shell, the Block row, the drafter and the icons as they are.
 *   Go-ahead: WITHOUT OBJECTIONS — no Objection Handling, no shared sections.
 *
 * The same shape as utils/outreach.js on purpose: a parent table and a child
 * table, each with its own clerk_user_id (supabase/0020), and everything above
 * `useCallScripts` pure so it can be tested without a round trip.
 */

import { useCallback, useMemo } from "react";
import { offerLine } from "./offerLine";
import { createRecordStore } from "./recordStore";
import { reorderWrites } from "./reorder";
import { applyLabelledBlock, ordinalWord, parseLabelledBlock, stripLabelledBlock } from "./sectionBlock";
import { wordCount } from "./websiteSectionKinds";

/* ── CALL OR VIDEO ──────────────────────────────────────────────────────────
   The first thing Add asks (2026-09-17). A video script is the same object as
   a call script — named sections, an offer, Done as her mark — so it lives in
   the same table with a `medium` (supabase/0024). Everything saved before that
   is a call, which is what the default says.                                  */
export const SCRIPT_MEDIUMS = [
  { id: "call", label: "Call", hint: "What you say to one person, or a small room, live." },
  { id: "video", label: "Video", hint: "What you say to camera — YouTube, LinkedIn, Reels." },
];

export const mediumOf = (script) => (script?.medium === "video" ? "video" : "call");
export const isVideo = (script) => mediumOf(script) === "video";

/* FORM is the qualifier that changes the writing, so it is asked on Add and it
   chooses the sections. Her words: "shortform or longform, then they could tag
   platform and purpose." A Reel has to earn its first second; a YouTube video
   has minutes and can teach properly. */
export const VIDEO_FORMS = [
  { id: "long", label: "Long-form", hint: "YouTube, LinkedIn, a webinar — minutes, room to teach.", sections: ["hook", "teaching", "ask"] },
  { id: "short", label: "Short-form", hint: "Reels, Shorts, TikTok — under a minute, one idea.", sections: ["hook", "teaching", "ask"] },
];

export function videoFormDef(id) {
  return VIDEO_FORMS.find((f) => f.id === id) || null;
}

/* PLATFORMS are TAGS, not the shape — one script can go to several, and each
   platform has its own rules Brill should respect (TikTok is not LinkedIn).
   She uses YouTube and LinkedIn, some Facebook; her clients may use the rest,
   which is why they are all here. */
export const VIDEO_PLATFORMS = [
  { id: "youtube", label: "YouTube" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "tiktok", label: "TikTok" },
  { id: "other", label: "Somewhere else" },
];

export function platformLabels(script) {
  const ids = Array.isArray(script?.platforms) ? script.platforms : [];
  return ids.map((id) => VIDEO_PLATFORMS.find((p) => p.id === id)?.label || id).filter(Boolean);
}

/* PURPOSE is the other tag: what this one is FOR. Four, because a fifth would
   be a category nobody picks. */
export const VIDEO_PURPOSES = [
  { id: "teach", label: "Teach", hint: "Show them how something works." },
  { id: "story", label: "Story", hint: "A moment that makes the point." },
  { id: "proof", label: "Proof", hint: "A result, a before and after." },
  { id: "offer", label: "Offer", hint: "Name the thing and ask." },
];

export function videoPurposeDef(id) {
  return VIDEO_PURPOSES.find((p) => p.id === id) || null;
}


/* ── What a call is for ──────────────────────────────────────────────────────
   Asked FIRST, on Add. `tier` is the offer type (momentum/offers.jsx
   OFFER_TYPES) a script of this type is allowed to connect to — answer a. */
/* ONE SET OF STAGE NAMES EVERYWHERE (her call, 2026-10-01): "in offers it
   said one thing, outreach another, and then scripts another." Trust Building
   · Core · Continuation, as the Offer Ladder says them — and she will describe
   her own program's three stages the same way. "Onboarding" was the Core label
   here and only named the first stretch of it; it stays in the description so
   it can still be found. The ids never move ("onboarding" stays stored). */
export const SCRIPT_TYPES = [
  { id: "trust", label: "Trust Building", tier: "trust", hint: "Calls that earn a yes — screening for fit, diagnosing, warming a room." },
  /* Any call inside the main engagement, not only the first (her note,
     2026-10-01: "it could include onboarding, but also check-in calls … a
     variety of things in that main engagement"). */
  { id: "onboarding", label: "Core", tier: "core", hint: "Calls in your main coaching program — onboarding, check-ins, coaching and teaching calls." },
  { id: "continuation", label: "Continuation", tier: "continuation", hint: "Calls that keep a client going after the main transformation." },
];

export function scriptTypeDef(id) {
  return SCRIPT_TYPES.find((t) => t.id === id) || null;
}

/* The starting calls from her mockup. Name, length and goal are hers; the
   sections are only what the mockup SHOWS — an opening on every call, and the
   qualifying questions on the Prequalifier. The spec does not list the rest
   ("don't guess"), so she adds them. Continuation has none yet, by the spec. */
export const SCRIPT_PRESETS = [
  { id: "prequalifier", type: "trust", name: "Prequalifier", duration: "15–20 min", goal: "Screen for fit", sections: ["opening", "qualifying"] },
  { id: "strategy_call", type: "trust", name: "Strategy Call", duration: "45 min", goal: "Diagnose & close", sections: ["opening"] },
  { id: "group_qa", type: "trust", name: "Group Q&A", duration: "60 min", goal: "Warm the room", sections: ["opening"] },
  { id: "foundation_review", type: "onboarding", name: "Foundation Review", duration: "30 min", goal: "Confirm foundations are in place", sections: ["opening"] },
  { id: "prelaunch_checkin", type: "onboarding", name: "Pre-Launch Check-in", duration: "20 min", goal: "Confirm readiness to launch", sections: ["opening"] },
];

export function presetDef(id) {
  return SCRIPT_PRESETS.find((p) => p.id === id) || null;
}

/** Offers a script of this type may connect to: the matching rung, not retired. */
export function offersForType(type, offers = [], medium = "call") {
  const live = (offers || []).filter((o) => o?.status !== "retired");
  /* A VIDEO is not a rung — it points at whatever it is selling, and the rung
     it serves is derived from that offer (utils/offerRung.js). So no filter. */
  if (medium === "video") return live;
  const tier = scriptTypeDef(type)?.tier;
  if (!tier) return [];
  return live.filter((o) => o?.type === tier);
}

/** The connected offer when it is on the WRONG rung for this script — made
 *  before a type change, say. Shown, never silently dropped. Null when fine. */
export function offerMismatch(script, offers = []) {
  if (!script?.offerId) return null;
  const offer = (offers || []).find((o) => o.id === script.offerId);
  const tier = scriptTypeDef(script.type)?.tier;
  if (!offer || !tier || offer.type === tier) return null;
  return offer;
}


/* ── Sections ────────────────────────────────────────────────────────────────
   Typed, each with its own fields (the spec: "type-specific field schemas, not
   one flat text box"). Opening and Qualifying questions are the two the spec
   defines. The full "+ Add section" vocabulary is an OPEN QUESTION in the
   spec, so the third kind is hers to name: one script field under a heading
   she types. Adding a kind here reaches the editor, the parser and Brill. */
export const SECTION_KINDS = [
  /* VIDEO — hook, teaching, ask (her words, 2026-09-17). Three sections, each
     with ONE field: a video script is spoken straight through, so splitting
     the teaching into sub-fields would fight how she reads it aloud. She
     renames them and adds her own, exactly as on a call.
     `medium` says which Add menu offers a kind; the ids are distinct from the
     call ones, so one lookup table still serves the editor and the parser. */
  {
    id: "hook",
    medium: "video",
    label: "Hook",
    hint: "The first line — the reason they stay",
    once: true,
    fields: [
      { key: "hook", block: "HOOK", label: "Hook", type: "prose", guide: "the first words out of your mouth" },
    ],
  },
  {
    id: "teaching",
    medium: "video",
    label: "Teaching",
    hint: "The middle — the one thing this video gives them",
    once: true,
    fields: [
      { key: "teaching", block: "TEACHING", label: "Teaching", type: "prose", guide: "what you say, in the order you say it" },
    ],
  },
  {
    id: "ask",
    medium: "video",
    label: "Ask",
    hint: "The end — what you want them to do",
    once: true,
    fields: [
      { key: "ask", block: "ASK", label: "Ask", type: "prose", guide: "one ask, said plainly" },
    ],
  },
  {
    id: "opening",
    medium: "call",
    label: "Opening",
    hint: "The first minute — how you open the call and set it up",
    once: true,
    fields: [
      { key: "opening_line", block: "OPENING_LINE", label: "Opening line", type: "prose", guide: "what you say first, word for word" },
    ],
  },
  {
    id: "qualifying",
    medium: "call",
    label: "Qualifying questions",
    hint: "Where they are, what staying there costs, and why now",
    once: true,
    fields: [
      { key: "current_state", block: "CURRENT_STATE", label: "Current state", type: "prose", guide: "questions about where they are now" },
      { key: "cost_of_inaction", block: "COST_OF_INACTION", label: "Cost of inaction", type: "prose", guide: "what it costs them to stay there" },
      { key: "why_now", block: "WHY_NOW", label: "Why now", type: "prose", guide: "why this is the moment" },
    ],
  },
  {
    id: "custom",
    label: "Your own section",
    hint: "Name it and write it — a transition, the offer, the close",
    once: false,
    fields: [
      { key: "script", block: "SCRIPT", label: "Script", type: "prose", guide: "what you say, word for word" },
    ],
  },
];

export const CUSTOM_KIND = "custom";

export function sectionKindDef(id) {
  return SECTION_KINDS.find((k) => k.id === id) || SECTION_KINDS.find((k) => k.id === CUSTOM_KIND);
}

/** What a section is called on screen: her name, else its kind's. */
export function sectionName(section) {
  return String(section?.name || "").trim() || sectionKindDef(section?.sectionType).label;
}

/** The kind as the shared Block and Drafter read it — its label is the
 *  section's own name, so a custom section reads as what she called it. */
export function sectionKindOf(section) {
  const kind = sectionKindDef(section?.sectionType);
  return { ...kind, id: kind.id, label: sectionName(section) };
}

/** The kinds she can still add: a once-only kind the script already has is
 *  left out; her own section is always there. */
/**
 * The kinds she can still add: a once-only kind the script already has is left
 * out, her own section is always there, and a kind belonging to the OTHER
 * medium is never offered — a call has no Hook and a video has no Qualifying
 * questions.
 */
export function addableKinds(sections = [], medium = "call") {
  const have = new Set((sections || []).map((s) => s.sectionType));
  return SECTION_KINDS
    .filter((k) => !k.medium || k.medium === medium)
    .filter((k) => !k.once || !have.has(k.id));
}

export function emptySectionContent(kindId) {
  const out = {};
  for (const f of sectionKindDef(kindId).fields) out[f.key] = "";
  return out;
}


/* ── What's written, and how much ────────────────────────────────────────────
   ONE count, used by the table, the rail, the header and every row — the spec's
   mockup note asked for exactly that. Counted off the stored values of the
   section's OWN fields; a field a kind does not show does not count. The same
   `wordCount` the website uses, so a word means the same thing on both. */
export function sectionWords(section) {
  const content = section?.content || {};
  return sectionKindDef(section?.sectionType).fields
    .reduce((n, f) => n + wordCount(content[f.key]), 0);
}

export function scriptWords(sections = []) {
  return (sections || []).reduce((n, s) => n + sectionWords(s), 0);
}

/* ── State, from ONE helper (rule 6) ─────────────────────────────────────────
   The same three words in the same order as pageState, moduleStatus and
   sequenceState. Done is her mark; nothing sets it for her. */
export const SCRIPT_STATE_LABEL = { done: "Done", draft: "Draft", empty: "Not written" };

export function scriptState(script, sections = []) {
  if (script?.status === "done") return "done";
  return (sections || []).some((s) => sectionWords(s) > 0) ? "draft" : "empty";
}

/** A section is written or it is not — no Done of its own (answer b). */
export function sectionState(section) {
  return sectionWords(section) > 0 ? "draft" : "empty";
}

export function scriptLabel(script) {
  /* An unnamed VIDEO reads as a video: "Untitled call script" on a Reel is
     the panel telling her it does not know what it holds (2026-09-17). */
  return String(script?.name || "").trim() || (isVideo(script) ? "Untitled video" : "Untitled call script");
}

/** Scripts in ladder order — Trust Building, Core, Continuation — then hers.
 *  The table bands them and the rail reads in the same order. */
export function groupScripts(scripts = []) {
  /* Sorted here, not trusted from the query: the store puts a row saved this
     session at the HEAD of its list. */
  const sorted = [...(scripts || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const calls = sorted.filter((s) => !isVideo(s));
  const groups = SCRIPT_TYPES.map((t) => ({
    key: t.id, label: t.label,
    rows: calls.filter((s) => s.type === t.id),
  }));
  /* VIDEOS ARE THEIR OWN BAND (2026-09-17). Without this they fell into "No
     type yet" — true of the call tier they do not have, and wrong about them:
     a video is not a rung, and the rung it serves is derived from its offer. */
  const videos = sorted.filter(isVideo);
  if (videos.length) groups.push({ key: "video", label: "Videos", rows: videos });
  const untyped = calls.filter((s) => !scriptTypeDef(s.type));
  if (untyped.length) groups.push({ key: "none", label: "No type yet", rows: untyped });
  return groups.filter((g) => g.rows.length);
}


/** The rows to write when a script is dragged from `from` to `to` inside the
 *  band `typeKey` (a groupScripts key). The whole list is renumbered 0..n-1 in
 *  band order, which also repairs tied sort_orders; a script never changes
 *  band, so a drag cannot change its type. Pure, for the tests. */
export function scriptReorderWrites(scripts = [], typeKey, from, to) {
  const groups = groupScripts(scripts);
  const band = groups.find((g) => g.key === typeKey);
  if (!band || from === to || from < 0 || to < 0 || from >= band.rows.length || to >= band.rows.length) return [];
  const rows = [...band.rows];
  rows.splice(to, 0, rows.splice(from, 1)[0]);
  const flat = groups.flatMap((g) => (g.key === typeKey ? rows : g.rows));
  return flat.map((s, i) => ({ ...s, sortOrder: i })).filter((s, i) => (flat[i].sortOrder ?? null) !== i);
}


/* ── Writing with Brill ──────────────────────────────────────────────────────
   The shared drafter, the Outreach way: labelled lines inside delimiters, and
   the field list read from SECTION_KINDS — api/_lib/prompts.js carries no copy. */

export const CALL_BLOCK = /<<<CALL_DRAFTED>>>([\s\S]*?)<<<CALL_END>>>/;

function specLines(section) {
  return sectionKindDef(section?.sectionType).fields.map((f) => `${f.block}: ${f.label.toLowerCase()} — ${f.guide}`);
}

function scriptLines(script, offers) {
  const offer = (offers || []).find((o) => o.id === script?.offerId);
  const offerAt = `Offer this ${isVideo(script) ? "video" : "call"} serves: ${offer ? offerLine(offer) : "none connected"}`;

  /* A VIDEO tells Brill its form, where it is going and what it is for —
     platform matters (TikTok is not LinkedIn) and form decides the pace. */
  if (isVideo(script)) {
    const form = videoFormDef(script?.form);
    const where = platformLabels(script);
    const purpose = videoPurposeDef(script?.purpose);
    return [
      `Video: ${scriptLabel(script)}`,
      `Form: ${form ? `${form.label} — ${form.hint}` : "not set yet"}`,
      `Where it goes: ${where.length ? where.join(", ") : "not set"}`,
      `What it is for: ${purpose ? `${purpose.label} — ${purpose.hint}` : "not set"}`,
      `Length: ${String(script?.duration || "").trim() || "not set"}`,
      `Goal: ${String(script?.goal || "").trim() || "not set"}`,
      offerAt,
    ];
  }

  const type = scriptTypeDef(script?.type);
  return [
    `Call: ${scriptLabel(script)}`,
    `What kind of call: ${type ? `${type.label} — ${type.hint}` : "not set yet"}`,
    `Length: ${String(script?.duration || "").trim() || "not set"}`,
    `Goal: ${String(script?.goal || "").trim() || "not set"}`,
    offerAt,
  ];
}

function sectionSays(section, labelFor = (f) => f.block) {
  const content = section?.content || {};
  return sectionKindDef(section?.sectionType).fields
    .map((f) => `${labelFor(f)}: ${String(content[f.key] || "").trim() || "(empty)"}`);
}

/** What Brill is told about ONE section: the call, this section, its labels,
 *  what it says now, and the other sections for fit. */
export function buildScriptSectionContext({ script, section, sections = [], offers = [] }) {
  const kind = sectionKindDef(section?.sectionType);
  const others = (sections || []).filter((s) => s.id !== section?.id);
  return [
    `[${isVideo(script) ? "VIDEO" : "CALL"}_CONTEXT — read only, do not reference as a conversation]`,
    ...scriptLines(script, offers),
    "",
    `This section: ${sectionName(section)} (${kind.label}) — ${kind.hint}`,
    "",
    "Fields to fill, one per line, using EXACTLY these labels:",
    ...specLines(section),
    "",
    "What this section says now:",
    ...sectionSays(section),
    "",
    `The other sections of this ${isVideo(script) ? "video" : "call"}, in order, for fit — do not rewrite them:`,
    ...(others.length
      ? others.map((s) => `${sectionName(s)}: ${sectionWords(s) ? sectionSays(s, (f) => f.label).join(" / ") : "(not written)"}`)
      : ["(none)"]),
  ].join("\n");
}

/** Every section's fields, each under a label unique to its place:
 *  SECTION_TWO_WHY_NOW. `key` is `${sectionId}::${field}`. */
export function scriptFields(sections = []) {
  const out = [];
  (sections || []).forEach((s, i) => {
    for (const f of sectionKindDef(s.sectionType).fields) {
      out.push({ ...f, key: `${s.id}::${f.key}`, block: `SECTION_${ordinalWord(i)}_${f.block}`, sectionId: s.id, field: f.key });
    }
  });
  return out;
}

export function buildScriptContext({ script, sections = [], offers = [] }) {
  const fields = scriptFields(sections);
  const lines = [
    `[${isVideo(script) ? "VIDEO_SCRIPT" : "CALL_SCRIPT"}_CONTEXT — read only, do not reference as a conversation]`,
    `Scope: THE WHOLE ${isVideo(script) ? "VIDEO" : "CALL"}. Write as many sections as you genuinely have words for, each under its own labels, all inside ONE block.`,
    ...scriptLines(script, offers),
    `Sections: ${(sections || []).length}`,
    "",
  ];
  (sections || []).forEach((s, i) => {
    const kind = sectionKindDef(s.sectionType);
    lines.push(`— Section ${i + 1} of ${sections.length}: ${sectionName(s)} (${kind.label}) — ${kind.hint}`);
    for (const f of fields.filter((x) => x.sectionId === s.id)) {
      lines.push(`  ${f.block} (${f.label.toLowerCase()} — ${f.guide}): ${String(s.content?.[f.field] || "").trim() || "(empty)"}`);
    }
    lines.push("");
  });
  lines.push("Labels to answer under (omit any section you are not changing):");
  lines.push(...fields.map((f) => f.block));
  return lines.join("\n");
}

export function parseScriptSectionBlock(text, section) {
  return parseLabelledBlock(text, sectionKindDef(section?.sectionType).fields, CALL_BLOCK);
}

export function applyScriptSectionBlock(content, block, section) {
  return applyLabelledBlock(content, block, sectionKindDef(section?.sectionType).fields);
}

/** { [sectionId]: { field: value } } for the sections Brill wrote, or null. */
export function parseScriptBlock(text, sections = []) {
  const fields = scriptFields(sections);
  const flat = parseLabelledBlock(text, fields, CALL_BLOCK);
  if (!flat) return null;
  const out = {};
  for (const f of fields) {
    if (flat[f.key] === undefined) continue;
    (out[f.sectionId] = out[f.sectionId] || {})[f.field] = flat[f.key];
  }
  return Object.keys(out).length ? out : null;
}

export function stripCallBlock(text) {
  return stripLabelledBlock(text, CALL_BLOCK);
}


/* ── The store ─────────────────────────────────────────────────────────────── */

export const SCRIPT_FIELDS = ["name", "medium", "form", "platforms", "purpose", "type", "preset", "duration", "goal", "offerId", "status", "sortOrder"];
export const SCRIPT_SECTION_FIELDS = ["scriptId", "sectionType", "name", "sortOrder", "content"];

const useScriptRecords = createRecordStore({
  table: "call_scripts",
  fields: SCRIPT_FIELDS,
  idPrefix: "cls",
  orderBy: "sort_order",
  ascending: true,
});

const useSectionRecords = createRecordStore({
  table: "call_script_sections",
  fields: SCRIPT_SECTION_FIELDS,
  idPrefix: "css",
  orderBy: "sort_order",
  ascending: true,
});

export function useCallScripts() {
  const {
    rows: scripts, loading: scriptsLoading, error: scriptsError,
    save: saveScript, remove: removeScript, clearError: clearScriptsError, newId: newScriptId,
  } = useScriptRecords();
  const {
    rows: sectionRows, loading: sectionsLoading, error: sectionsError,
    save: saveSection, remove: removeSection, clearError: clearSectionsError, newId: newSectionId,
  } = useSectionRecords();

  /* Every section of every script in one query — the table shows words and a
     dot per section on every row, so fetching per script would be a request
     inside a render loop. Sorted here: a row saved this session lands at the
     head of `rows`. */
  const byScript = useMemo(() => {
    const map = new Map();
    for (const s of sectionRows || []) {
      const list = map.get(s.scriptId) || [];
      list.push(s);
      map.set(s.scriptId, list);
    }
    for (const list of map.values()) list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    return map;
  }, [sectionRows]);

  const sectionsFor = useCallback((scriptId) => byScript.get(scriptId) || [], [byScript]);

  /** A new section of a kind, at the bottom. Returns it, or null. */
  const addSection = useCallback(async (script, kindId = CUSTOM_KIND) => {
    if (!script?.id) return null;
    const existing = sectionsFor(script.id);
    const last = existing.length ? Math.max(...existing.map((s) => s.sortOrder ?? 0)) : -1;
    return saveSection({
      id: newSectionId(), scriptId: script.id,
      sectionType: sectionKindDef(kindId).id, name: "",
      sortOrder: last + 1, content: emptySectionContent(kindId),
    });
  }, [sectionsFor, saveSection, newSectionId]);

  /** Drag a section to a new place; the script is renumbered 0..n-1. */
  const reorderSections = useCallback(async (scriptId, from, to) => {
    let ok = true;
    for (const row of reorderWrites(sectionsFor(scriptId), from, to)) {
      if (!(await saveSection(row))) ok = false;
    }
    return ok;
  }, [sectionsFor, saveSection]);

  /** A new script of a TYPE, from a starting call or blank, at the bottom.
   *  Sections are written one at a time — recordStore replaces rows one by one,
   *  and racing writes can interleave onto a stale list. */
  /**
   * Add a script. A CALL takes its type and maybe a preset, as it always did;
   * a VIDEO takes its form, and its sections come from that form — hook,
   * teaching, ask — so she lands on something to write rather than an empty
   * record (2026-09-17).
   */
  const addScript = useCallback(async (type, presetId = null, opts = {}) => {
    const medium = opts.medium === "video" ? "video" : "call";
    const form = medium === "video" ? (videoFormDef(opts.form)?.id || "long") : null;
    const preset = medium === "video" ? null : presetDef(presetId);
    const last = scripts.length ? Math.max(...scripts.map((s) => s.sortOrder ?? 0)) : -1;
    const saved = await saveScript({
      id: newScriptId(),
      name: preset?.name || "",
      medium,
      form,
      platforms: [],
      purpose: null,
      /* `type` is the CALL tier and stays null on a video: a video is not a
         rung of the ladder. */
      type: medium === "video" ? null : (type || preset?.type || null),
      preset: preset?.id || null,
      duration: preset?.duration || "",
      goal: preset?.goal || "",
      status: "draft",
      sortOrder: last + 1,
    });
    if (!saved) return null;
    let order = 0;
    const starting = medium === "video" ? (videoFormDef(form)?.sections || []) : (preset?.sections || []);
    for (const kindId of starting) {
      await saveSection({
        id: newSectionId(), scriptId: saved.id,
        sectionType: kindId, name: "", sortOrder: order++,
        content: emptySectionContent(kindId),
      });
    }
    return saved;
  }, [scripts, saveScript, newScriptId, saveSection, newSectionId]);

  /** Delete a script. The table cascades its sections, but the local list
   *  would keep them until a reload, so they go from here first. */
  const deleteScript = useCallback(async (id) => {
    for (const s of sectionsFor(id)) {
      if (!(await removeSection(s.id))) return false;
    }
    return removeScript(id);
  }, [sectionsFor, removeSection, removeScript]);

  /** A drag inside one type band (the Table and the rail both bands). */
  const reorderScripts = useCallback(async (typeKey, from, to) => {
    let ok = true;
    for (const row of scriptReorderWrites(scripts, typeKey, from, to)) {
      if (!(await saveScript(row))) ok = false;
    }
    return ok;
  }, [scripts, saveScript]);

  return {
    scripts,
    loading: scriptsLoading || sectionsLoading,
    error: scriptsError || sectionsError,
    clearError: () => { clearScriptsError(); clearSectionsError(); },
    sectionsFor,
    saveScript, addScript, deleteScript, reorderScripts,
    saveSection, addSection, removeSection, reorderSections,
  };
}
